import { lazy, Suspense, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type Konva from 'konva';
import { useAuth } from '../lib/useAuth';
import { uploadSnippet } from '../lib/upload';
import { buildInitialEditorState, editorReducer } from '../editor/state';
import type { ToolId } from '../editor/types';

const Canvas = lazy(() => import('../editor/Canvas'));
const Toolbar = lazy(() => import('../editor/Toolbar'));

type Draft = {
  mode: 'visible' | 'region' | 'desktop';
  dataUrl: string;
  width: number;
  height: number;
  createdAt: number;
};

type BridgeMessage = {
  type: 'snippits.draft';
  handoffId: string;
  draft: Draft;
};

const SHORTCUT_TO_TOOL: Record<string, ToolId> = {
  v: 'select',
  r: 'rect',
  a: 'arrow',
  l: 'line',
  p: 'pen',
  h: 'highlight',
  t: 'text',
  b: 'blur',
};

export default function Editor() {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const consumedRef = useRef(false);
  const stageRef = useRef<Konva.Stage | null>(null);

  const [state, dispatch] = useReducer(editorReducer, undefined, buildInitialEditorState);

  useEffect(() => {
    if (!draftId) return;

    const handler = (event: MessageEvent<BridgeMessage>) => {
      if (event.source !== window) return;
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'snippits.draft') return;
      if (event.data.handoffId !== draftId) return;
      setDraft(event.data.draft);
    };

    window.addEventListener('message', handler);
    window.postMessage(
      { type: 'snippits.draft.request', handoffId: draftId },
      window.location.origin,
    );

    const timer = window.setTimeout(() => setTimedOut(true), 4000);

    return () => {
      window.removeEventListener('message', handler);
      window.clearTimeout(timer);
    };
  }, [draftId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch({ type: 'redo' });
        return;
      }
      if (e.key === 'Escape') {
        dispatch({ type: 'select', id: null });
        dispatch({ type: 'setTool', tool: 'select' });
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.selectedId) {
          e.preventDefault();
          dispatch({ type: 'deleteShape', id: state.selectedId });
        }
        return;
      }
      if (!mod && !e.altKey && SHORTCUT_TO_TOOL[e.key.toLowerCase()]) {
        dispatch({ type: 'setTool', tool: SHORTCUT_TO_TOOL[e.key.toLowerCase()] });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.selectedId]);

  const onCancel = useCallback(() => {
    if (state.shapes.length > 0 && !window.confirm('Discard this snippit?')) return;
    if (draftId && !consumedRef.current) {
      consumedRef.current = true;
      window.postMessage(
        { type: 'snippits.draft.consume', handoffId: draftId },
        window.location.origin,
      );
    }
    navigate('/');
  }, [draftId, navigate, state.shapes.length]);

  const onDone = useCallback(async () => {
    if (!draft || !draftId) return;

    if (auth.status !== 'signed-in') {
      navigate(`/signin?next=${encodeURIComponent(`/editor/${draftId}`)}`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      dispatch({ type: 'select', id: null });
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      const stage = stageRef.current;
      let exportedDataUrl = draft.dataUrl;
      if (stage && state.shapes.length > 0) {
        const ratio = 1 / (stage.scaleX() || 1);
        exportedDataUrl = stage.toDataURL({ pixelRatio: ratio, mimeType: 'image/png' });
      }

      const result = await uploadSnippet({
        user: auth.user,
        dataUrl: exportedDataUrl,
        width: draft.width,
        height: draft.height,
      });

      if (!consumedRef.current) {
        consumedRef.current = true;
        window.postMessage(
          { type: 'snippits.draft.consume', handoffId: draftId },
          window.location.origin,
        );
      }

      try {
        await navigator.clipboard.writeText(result.shareUrl);
      } catch {
        // Clipboard might be blocked; viewer shows copy button.
      }

      navigate(`/s/${result.id}?justCreated=1`, { replace: true });
    } catch (err) {
      setUploading(false);
      setError((err as Error).message);
    }
  }, [auth, draft, draftId, navigate, state.shapes.length]);

  return (
    <div className="flex h-full flex-col bg-neutral-900">
      <div className="flex items-center justify-between border-b border-neutral-800 px-3 py-2">
        <div className="text-xs text-neutral-400">
          {draft ? `${draft.width} × ${draft.height}` : 'Editor'}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={uploading}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onDone}
            disabled={!draft || uploading || auth.status === 'loading'}
            className="rounded-md bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {uploading
              ? 'Uploading…'
              : auth.status === 'signed-in'
                ? 'Done & copy link'
                : 'Sign in & save'}
          </button>
        </div>
      </div>

      {draft && (
        <Suspense fallback={null}>
          <Toolbar state={state} dispatch={dispatch} />
        </Suspense>
      )}

      <div className="flex flex-1">
        {draft ? (
          <Suspense
            fallback={<div className="flex flex-1 items-center justify-center text-sm text-neutral-400">Loading editor…</div>}
          >
            <Canvas
              imageSrc={draft.dataUrl}
              imageWidth={draft.width}
              imageHeight={draft.height}
              state={state}
              dispatch={dispatch}
              stageRef={stageRef}
            />
          </Suspense>
        ) : timedOut ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="max-w-md text-center">
              <h1 className="text-lg font-medium">No draft found</h1>
              <p className="mt-2 text-sm text-neutral-400">
                This editor link only works right after the Snippits extension captures something.
                Make sure the extension is installed, then try capturing again.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-neutral-400">
            Waiting for capture…
          </div>
        )}
      </div>

      {error && (
        <div className="border-t border-red-900 bg-red-950 px-4 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
